import {
    Box,
    Chip,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { CommodityContractMonthMappingModel } from '../../../../Apis/ContractMonthMappingApi';
import { BudgetModel, CommodityModel, LocationModel, PositionModel, PositionType, Unit } from '../../../../Generated/Commodity-Management-Api';
import { FiscalMonth, convertVolume, convertPositionToUnit, getMappingsForCommodity } from '../../../../Helpers';
import { useLoadingState } from '../../../../Hooks';
import { StoneXLoading } from '../../../StoneX';
import { FuturesAllocationRow, buildChildRows, buildParentRows, positionDateRange, rolledUpChildren } from './FuturesAllocationReportLogic';

/**
 * Where futures were bought and how they have been rolled down to the locations that
 * take delivery.
 *
 * Futures are bought at the dashboard's location and budgets are split across its
 * children. An exchange for physical at a child sells the parent's futures and buys,
 * sells and takes physical at the child, with every leg allocated to that child's
 * group. The dashboard's own position list already has allocated volume taken out, so
 * this report loads the gross positions itself.
 *
 * Layout: the parent location gets fiscal-year totals, a chart of longs, rolls and net
 * against budget per contract month, and a table beneath. Each child location gets its
 * own card with physical delivered against its budget share. Budget months are folded
 * into the contract months they price off through the commodity's contract month
 * mapping.
 *
 * Some children trade their own futures and swaps and never roll anything through an
 * exchange for physical. Those are flagged in the report settings; their hedges are
 * counted in the parent's table as well as on their own card.
 */

const COLORS = {
    long: '#283593',
    short: '#9fa8da',
    net: '#5c6bc0',
    budget: '#d32f2f',
    physical: '#26a69a',
    muted: '#9e9e9e',
};

interface FuturesAllocationReportProps {
    /** The location whose dashboard this is; its children are the tabs. */
    parentLocationId: number;
    locations: LocationModel[];
    childLocations: LocationModel[];
    commodity?: CommodityModel;
    /** Every budget for the fiscal year, in its original units. */
    budgets: BudgetModel[];
    fiscalYearMonths: FiscalMonth[];
    /** Every commodity's budget month to contract month mapping for the site. */
    contractMonthMappings: CommodityContractMonthMappingModel[];
    units?: Unit[];
    /** Children whose futures and swaps are counted with the parent; see FuturesAllocationSettings. */
    locationIdsWithoutEfp?: number[];
}

export default function FuturesAllocationReport(props: FuturesAllocationReportProps) {
    const { parentLocationId, locations, childLocations, commodity, budgets, fiscalYearMonths, contractMonthMappings, units, locationIdsWithoutEfp } = props;

    const [displayUnit, setDisplayUnit] = useState<Unit>(Unit.Contract);
    const [grossPositions, setGrossPositions] = useState<PositionModel[]>([]);
    const loadingState = useLoadingState();

    const parentLocation = useMemo(() => locations.find(l => l.locationId === parentLocationId), [locations, parentLocationId]);
    const mappings = useMemo(() => getMappingsForCommodity(contractMonthMappings, commodity?.commodityId), [contractMonthMappings, commodity?.commodityId]);
    const dateRange = useMemo(() => positionDateRange(fiscalYearMonths, mappings), [fiscalYearMonths, mappings]);

    const unitOptions = useMemo(() => {
        const options = [...(units ?? [])];
        if (!options.includes(Unit.Contract)) options.unshift(Unit.Contract);
        return options;
    }, [units]);

    // Gross positions: every leg of an exchange for physical is allocated to the child's
    // group, so the dashboard's offset list would hide exactly the trades this report
    // is about. The budget date range is widened to any contract month outside the
    // fiscal year, because an unassigned future sits on its contract month.
    useEffect(() => {
        if (!commodity || !dateRange) {
            setGrossPositions([]);
            return;
        }

        const { from: first, to: last } = dateRange;
        let cancelled = false;

        CommodityManagementApi.listPositions(
            loadingState.setLoadingState,
            undefined, // quoteDate
            false, // includeExpirationDates
            false, // includeMarketPrices
            false, // useDisplayUnits - keep original units so contract sizes convert correctly
            undefined, // scenarioMarketPrices
            [commodity.commodityId],
            undefined, // locationsFilter
            undefined, // counterpartiesFilter
            undefined, // tradeDateStartFilter
            undefined, // tradeDateEndFilter
            first.year,
            first.month,
            undefined,
            last.year,
            last.month,
            undefined,
            undefined, undefined, undefined, // contract month start
            undefined, undefined, undefined, // contract month end
            [PositionType.Future, PositionType.Swap, PositionType.Physical],
            undefined, // dealTicketNumberFilter
            undefined, // dealTicketIdFilter
            undefined, // sideFilter
            undefined, // validatedFilter
            undefined, // currencyFilter
            undefined, // currency
            undefined, // expiryStatus
            false, // includeAllocations
            false, // includeAllocationOffset - gross volumes
        )
            .then(response => {
                if (!cancelled) setGrossPositions(response.data.rows ?? []);
            })
            .catch(error => {
                console.error('Failed to fetch positions for the futures allocation report', error);
                if (!cancelled) setGrossPositions([]);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commodity?.commodityId, dateRange]);

    const { parent, children } = useMemo(() => {
        const positions = grossPositions.map(p => convertPositionToUnit(p, displayUnit));

        const commodityBudgets = budgets
            .filter(b => b.commodity?.commodityId === commodity?.commodityId)
            .map(b => {
                const strategy = b.budgetPricingStrategy;
                return {
                    ...b,
                    volume: convertVolume(b.volume ?? 0, b.unit, displayUnit, strategy?.bushelVolume ?? undefined, strategy?.contractVolume ?? undefined, strategy?.contractUnit ?? undefined),
                };
            });

        const shared = { fiscalYearMonths, mappings, childLocations, positions, budgets: commodityBudgets, locationIdsWithoutEfp };
        return {
            parent: parentLocation ? buildParentRows({ ...shared, parentLocation }) : undefined,
            children: buildChildRows(shared),
        };
    }, [grossPositions, budgets, commodity?.commodityId, displayUnit, fiscalYearMonths, mappings, parentLocation, childLocations, locationIdsWithoutEfp]);

    // Children flagged in the report settings as trading their own hedges.
    const rolledUp = useMemo(() => rolledUpChildren(childLocations, locationIdsWithoutEfp), [childLocations, locationIdsWithoutEfp]);
    const parentSubtitle = rolledUp.length > 0
        ? `Where the futures are bought, including futures and swaps traded at ${rolledUp.map(l => l.locationName).join(', ')}. Volumes in ${displayUnit}.`
        : `Where the futures are bought. Volumes in ${displayUnit}.`;

    // The parent usually only holds futures; its Physical column is noise unless
    // something was actually delivered there.
    const parentHasPhysical = parent?.rows.some(row => row.physicalVolume !== 0) ?? false;

    const decimals = displayUnit === Unit.Contract ? 2 : 0;
    const format = (value: number) => formatVolume(value, decimals);

    // The child rows come back as a flat tree: a location row followed by its months.
    const childSections = useMemo(
        () =>
            childLocations.map(child => ({
                location: child,
                summary: children.rows.find(r => r.path.length === 1 && r.path[0] === child.locationName),
                months: children.rows.filter(r => r.path.length === 2 && r.path[0] === child.locationName),
            })),
        [children, childLocations],
    );

    // The section heading names the tier the children sit in (Plant, Region...), falling
    // back to a generic label when the tier has no name.
    const childTierName = useMemo(() => {
        const names = Array.from(new Set(childLocations.map(l => l.tierName).filter((n): n is string => !!n)));
        return names.length > 0 ? names.join(' / ') : 'Locations';
    }, [childLocations]);

    const ready = !loadingState.isLoading() && !loadingState.failed() && parent !== undefined;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>Futures Allocation</Typography>
                <FormControl sx={{ minWidth: 140 }} size="small">
                    <InputLabel id="futures-allocation-unit-label">Unit</InputLabel>
                    <Select
                        labelId="futures-allocation-unit-label"
                        value={displayUnit}
                        label="Unit"
                        onChange={e => setDisplayUnit(e.target.value as Unit)}
                    >
                        {unitOptions.map(unit => (
                            <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <StoneXLoading show={loadingState.isLoading()} />

            {loadingState.failed() && <Typography color="error">The positions for this report could not be loaded.</Typography>}

            {ready && parent && parentLocation && (
                <>
                    <SectionCard title={parentLocation.locationName} subtitle={parentSubtitle}>
                        <KpiRow
                            items={[
                                { label: 'Long Futures', value: format(parent.total.longFutures), color: COLORS.long, hint: 'Bought for the fiscal year' },
                                { label: 'EFP', value: format(parent.total.shortFutures), color: COLORS.short, hint: 'Sold through exchange for physical' },
                                { label: 'Net Held', value: format(parent.total.netFutures), color: COLORS.net, hint: 'Still held at this location' },
                                { label: 'Budget', value: format(parent.total.budgetVolume), color: COLORS.budget, hint: 'Across all locations below' },
                                { label: 'Covered', value: formatPercent(parent.total.longFutures, parent.total.budgetVolume), color: COLORS.muted, hint: 'Long futures as a share of budget, before any EFPs' },
                                ...(parentHasPhysical ? [{ label: 'Physical', value: format(parent.total.physicalVolume), color: COLORS.physical, hint: 'Delivered here directly' }] : []),
                            ]}
                        />

                        <ParentChart rows={parent.rows} showPhysical={parentHasPhysical} decimals={decimals} />

                        <VolumeTable
                            rows={parent.rows}
                            total={parent.total}
                            decimals={decimals}
                            columns={[
                                { key: 'longFutures', label: 'Long', color: COLORS.long },
                                { key: 'shortFutures', label: 'EFP', color: COLORS.short },
                                { key: 'netFutures', label: 'Net Held', color: COLORS.net, emphasis: true },
                                { key: 'budgetVolume', label: 'Budget', color: COLORS.budget },
                                ...(parentHasPhysical ? [{ key: 'physicalVolume' as const, label: 'Physical', color: COLORS.physical }] : []),
                            ]}
                            coverage={{ label: 'Covered', numerator: 'longFutures', denominator: 'budgetVolume', color: COLORS.long }}
                        />
                    </SectionCard>

                    {childSections.length > 0 && (
                        <>
                            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>{childTierName}</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
                                {childSections.map(({ location, summary, months }) => (
                                    <ChildCard
                                        key={location.locationId}
                                        location={location}
                                        summary={summary}
                                        months={months}
                                        decimals={decimals}
                                        hedgesCountedAt={rolledUp.some(l => l.locationId === location.locationId) ? parentLocation.locationName : undefined}
                                    />
                                ))}
                            </Box>
                        </>
                    )}
                </>
            )}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Presentation pieces
// ---------------------------------------------------------------------------

function formatVolume(value: number, decimals: number): string {
    if (value === 0) return '–';
    return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function formatPercent(numerator: number, denominator: number): string {
    if (!denominator) return '–';
    return `${Math.round((numerator / denominator) * 100)}%`;
}

function SectionCard(props: { title: string; subtitle?: string; action?: ReactNode; children: ReactNode }) {
    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{props.title}</Typography>
                    {props.subtitle && <Typography variant="caption" color="text.secondary">{props.subtitle}</Typography>}
                </Box>
                {props.action}
            </Box>
            {props.children}
        </Paper>
    );
}

interface KpiItem {
    label: string;
    value: string;
    color: string;
    hint?: string;
}

function KpiRow({ items }: { items: KpiItem[] }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(150px, 1fr))`, gap: 2, mb: 3 }}>
            {items.map(item => (
                <Box key={item.label} sx={{ p: 2, borderRadius: 2, backgroundColor: '#fafafa', borderTop: `4px solid ${item.color}` }}>
                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>{item.label}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{item.value}</Typography>
                    {item.hint && <Typography variant="caption" color="text.secondary">{item.hint}</Typography>}
                </Box>
            ))}
        </Box>
    );
}

type VolumeKey = 'longFutures' | 'shortFutures' | 'netFutures' | 'budgetVolume' | 'physicalVolume';

function chartTooltipFormatter(decimals: number) {
    return (value: unknown) => (typeof value === 'number' ? formatVolume(value, decimals) : String(value ?? ''));
}

function ParentChart({ rows, showPhysical, decimals }: { rows: FuturesAllocationRow[]; showPhysical: boolean; decimals: number }) {
    const data = rows.map(row => ({ month: row.path[0], ...row }));

    return (
        <Box sx={{ width: '100%', height: 300, mb: 3 }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(value: number) => value.toLocaleString()} />
                    <Tooltip formatter={chartTooltipFormatter(decimals)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="longFutures" name="Long" fill={COLORS.long} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="shortFutures" name="EFP" fill={COLORS.short} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="netFutures" name="Net Held" fill={COLORS.net} radius={[3, 3, 0, 0]} />
                    {showPhysical && <Bar dataKey="physicalVolume" name="Physical" fill={COLORS.physical} radius={[3, 3, 0, 0]} />}
                    <Line type="monotone" dataKey="budgetVolume" name="Budget" stroke={COLORS.budget} strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3, fill: COLORS.budget }} />
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
}

function ChildChart({ rows, decimals }: { rows: FuturesAllocationRow[]; decimals: number }) {
    const data = rows.map(row => ({ month: row.path[1], ...row }));

    return (
        <Box sx={{ width: '100%', height: 200, mb: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(value: number) => value.toLocaleString()} />
                    <Tooltip formatter={chartTooltipFormatter(decimals)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="physicalVolume" name="Physical" fill={COLORS.physical} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="netFutures" name="Net Futures" fill={COLORS.net} radius={[3, 3, 0, 0]} />
                    <Line type="monotone" dataKey="budgetVolume" name="Budget" stroke={COLORS.budget} strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3, fill: COLORS.budget }} />
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
}

interface VolumeColumn {
    key: VolumeKey;
    label: string;
    color: string;
    /** Bold the value and colour it by sign. */
    emphasis?: boolean;
}

interface CoverageColumn {
    label: string;
    numerator: VolumeKey;
    denominator: VolumeKey;
    color: string;
}

function VolumeTable(props: {
    rows: FuturesAllocationRow[];
    total: FuturesAllocationRow;
    decimals: number;
    columns: VolumeColumn[];
    coverage?: CoverageColumn;
    /** Which path segment names the row. */
    labelIndex?: number;
    size?: 'small' | 'medium';
}) {
    const { rows, total, decimals, columns, coverage, labelIndex = 0, size = 'medium' } = props;

    const cell = (row: FuturesAllocationRow, column: VolumeColumn) => {
        const value = row[column.key];
        const color = column.emphasis && value !== 0 ? (value > 0 ? COLORS.long : COLORS.budget) : value === 0 ? 'text.disabled' : 'text.primary';
        return (
            <TableCell key={column.key} align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: column.emphasis ? 600 : 400, color }}>
                {formatVolume(value, decimals)}
            </TableCell>
        );
    };

    const coverageCell = (row: FuturesAllocationRow) => {
        if (!coverage) return null;
        const numerator = row[coverage.numerator];
        const denominator = row[coverage.denominator];
        const ratio = denominator ? numerator / denominator : 0;
        return (
            <TableCell key="coverage" sx={{ minWidth: 160 }}>
                {denominator ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LinearProgress
                            variant="determinate"
                            value={Math.max(0, Math.min(100, ratio * 100))}
                            sx={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: '#eeeeee', '& .MuiLinearProgress-bar': { backgroundColor: ratio > 1 ? COLORS.budget : coverage.color, borderRadius: 4 } }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {formatPercent(numerator, denominator)}
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.disabled">–</Typography>
                )}
            </TableCell>
        );
    };

    return (
        <TableContainer>
            <Table size={size} sx={{ '& td, & th': { borderColor: 'divider' } }}>
                <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 } }}>
                        <TableCell>Contract Month</TableCell>
                        {columns.map(column => (
                            <TableCell key={column.key} align="right">
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: column.color }} />
                                    {column.label}
                                </Box>
                            </TableCell>
                        ))}
                        {coverage && <TableCell>{coverage.label}</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <TableRow key={row.path.join('/')} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                            <TableCell sx={{ fontWeight: 500 }}>{row.path[labelIndex]}</TableCell>
                            {columns.map(column => cell(row, column))}
                            {coverageCell(row)}
                        </TableRow>
                    ))}
                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderTopColor: 'text.primary', backgroundColor: '#f5f5f5' } }}>
                        <TableCell>Total</TableCell>
                        {columns.map(column => cell(total, column))}
                        {coverageCell(total)}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function ChildCard(props: {
    location: LocationModel;
    summary: FuturesAllocationRow | undefined;
    months: FuturesAllocationRow[];
    decimals: number;
    /** Set when this child's futures and swaps are also counted in the named parent's table. */
    hedgesCountedAt?: string;
}) {
    const { location, summary, months, decimals, hedgesCountedAt } = props;
    const format = (value: number) => formatVolume(value, decimals);

    const netted = (summary?.netFutures ?? 0) === 0;
    const tradedFutures = (summary?.longFutures ?? 0) !== 0 || (summary?.shortFutures ?? 0) !== 0;

    const status = hedgesCountedAt
        ? <Chip size="small" label={`Futures also counted with ${hedgesCountedAt}`} color="primary" variant="outlined" />
        : !tradedFutures
            ? <Chip size="small" label="No futures rolled here" variant="outlined" />
            : netted
                ? <Chip size="small" label="Futures netted to zero" color="success" variant="outlined" />
                : <Chip size="small" label={`Net futures open: ${format(summary!.netFutures)}`} color="warning" variant="outlined" />;

    const subtitle = hedgesCountedAt
        ? `Trades its own futures and swaps, which are also counted under ${hedgesCountedAt}.`
        : "Physical delivered against this location's share of the budget.";

    return (
        <SectionCard title={location.locationName} subtitle={subtitle} action={status}>
            {summary && (
                <KpiRow
                    items={[
                        { label: 'Physical', value: format(summary.physicalVolume), color: COLORS.physical },
                        { label: 'Budget Share', value: format(summary.budgetVolume), color: COLORS.budget },
                        { label: 'Delivered', value: formatPercent(summary.physicalVolume, summary.budgetVolume), color: COLORS.muted },
                    ]}
                />
            )}

            <ChildChart rows={months} decimals={decimals} />

            {summary && (
                <VolumeTable
                    rows={months}
                    total={summary}
                    decimals={decimals}
                    size="small"
                    labelIndex={1}
                    columns={[
                        { key: 'longFutures', label: 'Long', color: COLORS.long },
                        { key: 'shortFutures', label: 'Short', color: COLORS.short },
                        { key: 'netFutures', label: 'Net', color: COLORS.net, emphasis: true },
                        { key: 'budgetVolume', label: 'Budget', color: COLORS.budget },
                        { key: 'physicalVolume', label: 'Physical', color: COLORS.physical },
                    ]}
                    coverage={{ label: 'Delivered', numerator: 'physicalVolume', denominator: 'budgetVolume', color: COLORS.physical }}
                />
            )}
        </SectionCard>
    );
}
